import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Tab,
    Tabs,
    Card,
    Table,
    Button,
    Divider,
    TableBody,
    Container,
    TableContainer,
    Box,
    CircularProgress,
    Typography,
    TextField,
    MenuItem,
} from '@mui/material';
import { PATH_MASTERDATA } from '../../../routes/paths';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import CustomBreadcrumbs from '../../../components/custom-breadcrumbs';
import { useSettingsContext } from '../../../components/settings';
import {
    TableNoData,
    TableHeadCustom,
    TablePaginationCustom,
} from '../../../components/table';
import { useSnackbar } from '../../../components/snackbar';
import siteAPI from 'src/api/siteAPI';
import DynamicTableRow from 'src/sections/@dashboard/DynamicTableRow';
import TableToolbar from 'src/sections/@dashboard/TableToolBar';
import RoleBasedGuard from 'src/auth/RoleBasedGuard';
import Label from 'src/components/label';
import usePaginatedTable from 'src/hooks/usePaginatedTable';

// ----------------------------------------------------------------------

const STATUS_OPTIONS = ['all', 'active', 'inactive'];

const TABLE_HEAD = [
    { id: 'name', label: 'Name', align: 'center' },
    { id: 'location', label: 'Location', align: 'center' },
    { id: 'status', label: 'Status', align: 'center' },
    { id: 'payroll', label: 'Payroll', align: 'center' },
    { id: '' },
];

// ----------------------------------------------------------------------

export default function SitePage() {
    const { enqueueSnackbar } = useSnackbar();
    const { themeStretch } = useSettingsContext();
    const navigate = useNavigate();

    // 🔹 Filter tab state
    const [filterStatus, setFilterStatus] = useState('all');

    // 🔹 Implementasi hook universal
    const {
        data: tableData,
        loading,
        pagination,
        setPage,
        setPerPage,
        filters,
        setFilters,
        setFilterValue,
        sort,
        handleSort,
        refresh: fetchData,
    } = usePaginatedTable({
        apiFn: siteAPI.getSites, // pastikan endpoint mendukung pagination dan filter
        initialPage: 1,
        initialPerPage: 10,
        initialFilter: { search: '', is_active: null },
    });

    // 🔹 Handler: tab status
    const handleFilterStatus = (event, newValue) => {
        setPage(1);
        setFilterStatus(newValue);

        if (newValue === 'all') {
            setFilterValue('is_active', null);
        } else if (newValue === 'active') {
            setFilterValue('is_active', '1');
        } else if (newValue === 'inactive') {
            setFilterValue('is_active', '0');
        }
    };

    // 🔹 Handler: search name/location
    const handleFilterName = (event) => {
        setPage(1);
        setFilterValue('search', event.target.value);
    };

    // 🔹 Reset semua filter
    const handleResetFilter = () => {
        setFilters({ search: '', is_active: null });
        setFilterStatus('all');
        setPage(1);
    };

    // 🔹 Delete site
    const handleDeleteRow = async (id) => {
        try {
            await siteAPI.deleteSite(id);
            enqueueSnackbar('Site deleted successfully', { variant: 'success' });
            fetchData(); // refresh data
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Failed to delete site', { variant: 'error' });
        }
    };

    // 🔹 Edit site
    const handleEditRow = (id) => {
        navigate(PATH_MASTERDATA.siteEdit(id));
    };

    const isNotFound = !loading && tableData.length === 0;

    // ----------------------------------------------------------------------

    return (
        <>
            <Helmet>
                <title>Site: List | Deposit Monitoring System</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : 'lg'}>
                <CustomBreadcrumbs
                    heading="Site List"
                    links={[
                        { name: 'Master Data', href: PATH_MASTERDATA.root },
                        { name: 'Site', href: PATH_MASTERDATA.site },
                        { name: 'List' },
                    ]}
                    action={
                        <RoleBasedGuard roles={[1, 2, 4, 6]}>
                            <Button
                                component={RouterLink}
                                to={PATH_MASTERDATA.siteAdd}
                                variant="contained"
                                startIcon={<Iconify icon="eva:plus-fill" />}
                            >
                                New Site
                            </Button>
                        </RoleBasedGuard>
                    }
                />

                <Card>
                    {/* 🔹 Tabs: filter status */}
                    <Tabs
                        value={filterStatus}
                        onChange={handleFilterStatus}
                        sx={{
                            px: 2,
                            bgcolor: 'background.neutral',
                        }}
                    >
                        {STATUS_OPTIONS.map((tab) => (
                            <Tab key={tab} label={tab} value={tab} />
                        ))}
                    </Tabs>

                    {/* 🔹 Toolbar: search */}
                    <TableToolbar
                        filterText={filters.search || ''}
                        onFilterText={handleFilterName}
                        onResetFilter={handleResetFilter}
                        placeholder="Search site name or location..."
                    />

                    <Divider />

                    {/* 🔹 Table */}
                    <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                        {loading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: 300,
                                    flexDirection: 'column',
                                }}
                            >
                                <CircularProgress color="primary" />
                                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                    Loading site data...
                                </Typography>
                            </Box>
                        ) : (
                            <Scrollbar>
                                <Table size="small" sx={{ minWidth: 800 }}>
                                    <TableHeadCustom
                                        order={sort.orderDirection}
                                        orderBy={sort.orderBy}
                                        headLabel={TABLE_HEAD}
                                        onSort={handleSort}
                                    />

                                    <TableBody>
                                        {tableData.map((row) => (
                                            <DynamicTableRow
                                                key={row.id}
                                                row={{
                                                    id: row.id,
                                                    name: row.name,
                                                    location: row.location,
                                                    status: (
                                                        <Label color={row.is_active ? 'success' : 'error'}>
                                                            {row.is_active ? 'Active' : 'Inactive'}
                                                        </Label>
                                                    ),
                                                    payroll: (
                                                        <Label color={row.is_payroll ? 'default' : 'default'}>
                                                            {row.is_payroll ? '2x' : '1x'}
                                                        </Label>
                                                    )
                                                }}
                                                columns={TABLE_HEAD}
                                                enableAvatar
                                                onRowClick={() => navigate(PATH_MASTERDATA.siteDetail(row.id))}
                                                onEditRow={() => handleEditRow(row.id)}
                                                onDeleteRow={() => handleDeleteRow(row.id)}
                                            />
                                        ))}

                                        <TableNoData isNotFound={isNotFound} />
                                    </TableBody>
                                </Table>
                            </Scrollbar>
                        )}
                    </TableContainer>

                    {/* 🔹 Pagination */}
                    {!loading && (
                        <TablePaginationCustom
                            count={pagination.total}
                            page={pagination.page - 1}
                            rowsPerPage={pagination.perPage}
                            onPageChange={(_, newPage) => setPage(newPage + 1)}
                            onRowsPerPageChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                        />
                    )}
                </Card>
            </Container>
        </>
    );
}
